/**
 * Preferences Hooks - React Query hooks for user and system preferences
 * 
 * User Preferences:
 * - useUserPreferences() - Get current user's preferences
 * - useUpdateUserPreferences() - Update current user's preferences
 * 
 * System Preferences (admin only):
 * - useSystemPreferences() - Get system preferences
 * - useUpdateSystemPreferences() - Update system preferences
 * - useAddApprovedDomain() - Add domain to approved list
 * - useRemoveApprovedDomain() - Remove domain from approved list
 * 
 * User Management (admin only):
 * - useUsers() - List all users
 * - useUpdateUserRole() - Update a user's role
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { Info as UserPreferencesInfo } from '@sbclite/core/auth/user-preferences';
import type { Info as SystemPreferencesInfo } from '@sbclite/core/auth/system-preferences';
import type { Info as McpTokenInfo } from '@sbclite/core/auth/mcp-token';
import type { UserListItem, CurrentUserInfo } from '../types/user';

// ========================================
// USER PREFERENCES HOOKS
// ========================================

/**
 * Get current user's preferences
 */
export function useUserPreferences() {
  return useQuery({
    queryKey: ['userPreferences'],
    queryFn: async (): Promise<UserPreferencesInfo> => {
      const response = await api.getUserPreferences();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update current user's preferences
 */
export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<UserPreferencesInfo>) => {
      const response = await api.updateUserPreferences(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPreferences'] });
    },
  });
}

// ========================================
// SYSTEM PREFERENCES HOOKS (Admin only)
// ========================================

/**
 * Get system preferences
 */
export function useSystemPreferences() {
  return useQuery({
    queryKey: ['systemPreferences'],
    queryFn: async (): Promise<SystemPreferencesInfo> => {
      const response = await api.getSystemPreferences();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update system preferences
 */
export function useUpdateSystemPreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<SystemPreferencesInfo>) => {
      const response = await api.updateSystemPreferences(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemPreferences'] });
    },
  });
}

/**
 * Add approved domain
 */
export function useAddApprovedDomain() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (domain: string) => {
      const response = await api.addApprovedDomain(domain);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemPreferences'] });
    },
  });
}

/**
 * Remove approved domain
 */
export function useRemoveApprovedDomain() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (domain: string) => {
      const response = await api.removeApprovedDomain(domain);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemPreferences'] });
    },
  });
}

// ========================================
// USER MANAGEMENT HOOKS (Admin only)
// ========================================

/**
 * List all users (admin only)
 */
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<UserListItem[]> => {
      const response = await api.getUsers();
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Update user role (admin only)
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'admin' | 'user' }) => {
      const response = await api.updateUserRole(userId, role);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ========================================
// CURRENT USER INFO HOOK
// ========================================

// Re-exported from the shared leaf module so existing importers keep working.
export type { UserListItem, CurrentUserInfo } from '../types/user';

/**
 * Get current user info with preferences
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async (): Promise<CurrentUserInfo> => {
      const response = await api.getCurrentUser();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update current user's profile (name)
 */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const response = await api.updateUserProfile(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

// ========================================
// EMAIL CONFIGURATION HOOKS (Admin only)
// ========================================

export interface EmailConfigInfo {
  resendApiKeyConfigured: boolean;
  resendApiKeyMasked: string | null;
  resendEnvLocked: boolean;
  emailSenderDomain: string;
  emailSenderName: string;
  emailVerificationEnabled: boolean;
}

/**
 * Get email configuration (admin only)
 */
export function useEmailConfig() {
  return useQuery({
    queryKey: ['emailConfig'],
    queryFn: async (): Promise<EmailConfigInfo> => {
      const response = await api.getEmailConfig();
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Update email configuration (admin only)
 */
export function useUpdateEmailConfig() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      resendApiKey?: string | null;
      emailSenderDomain?: string;
      emailSenderName?: string;
      emailVerificationEnabled?: boolean;
    }) => {
      const response = await api.updateEmailConfig(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailConfig'] });
      queryClient.invalidateQueries({ queryKey: ['systemPreferences'] });
    },
  });
}

/**
 * Send test email (admin only)
 */
export function useSendTestEmail() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.sendTestEmail();
      return response.data;
    },
  });
}

// ========================================
// MCP TOKEN HOOKS (Admin only)
// ========================================

export function useMcpTokens() {
  return useQuery({
    queryKey: ['mcp-tokens'],
    queryFn: async (): Promise<McpTokenInfo[]> => {
      const response = await api.getMcpTokens();
      return response.data;
    },
    staleTime: 30 * 1000,
  });
}

export function useCreateMcpToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const response = await api.createMcpToken(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
  });
}

export function useRevokeMcpToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.revokeMcpToken(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
  });
}

// ========================================
// EMAIL VERIFICATION HOOKS
// ========================================

/**
 * Check if user can resend verification email
 * Returns the user's email if they can resend
 */
export function useCheckResendVerification() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.checkResendVerification();
      return response.data;
    },
  });
}
